import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { CostGroupEnum } from 'gql/graphql';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    bimCreateWorkOrderActsKC2GroupGql,
    bimUpdateWorkOrderActsKC2GroupGql,
} from '../BimWorkOrderActsKc2ListQueries';

export type TGroupData = {
    headerCheckbox: boolean | 'intermediate' | undefined;
    actKC2Ids: Record<string, boolean>;
    groupName: string | undefined;
};

const idMapper = {
    [CostGroupEnum.Cmr]: 'actKC2Id',
    [CostGroupEnum.Others]: 'actKC2OtherId',
};

const transformActInputNameToIds = (actKC2Ids: Record<string, boolean>) =>
    Object.keys(actKC2Ids)
        .filter((key) => actKC2Ids[key])
        .map((keyIdPairs) => {
            const [key, id] = keyIdPairs.split('_');

            return {
                [idMapper[key as CostGroupEnum]]: +id,
            };
        });

export const useBimActsKc2GroupSaver = ({
    workOrderId,
    onSuccess,
    actKC2GroupId,
}: {
    workOrderId: number;
    actKC2GroupId: number | null;
    onSuccess: () => void;
}) => {
    const dispatch = useDispatch();

    const [resetCreateData] = useResetStoreData({
        requestId: 'bimActsKc2GroupCreate',
    });
    const [resetUpdateData] = useResetStoreData({
        requestId: 'bimActsKc2GroupUpdate',
    });

    const handleSuccess = useCallback(() => {
        if (!actKC2GroupId) {
            resetCreateData(undefined);
        }
        if (actKC2GroupId) {
            resetUpdateData(undefined);
        }
        onSuccess();
    }, [actKC2GroupId, onSuccess, resetCreateData, resetUpdateData]);

    const [, createGroupStatus, , createGroupRequest] = useRequest({
        requestId: 'bimActsKc2GroupCreate',
        query: bimCreateWorkOrderActsKC2GroupGql,
        withEffect: false,
        dependenciesUpdater: handleSuccess,
    });

    const [, updateGroupStatus, , updateGroupRequest] = useRequest({
        requestId: 'bimActsKc2GroupUpdate',
        query: bimUpdateWorkOrderActsKC2GroupGql,
        withEffect: false,
        dependenciesUpdater: handleSuccess,
    });

    const handleSaveGroup = useCallback(
        (formData: TGroupData) => {
            const { actKC2Ids, groupName } = formData;

            if (actKC2Ids && groupName) {
                if (!actKC2GroupId) {
                    dispatch(
                        createGroupRequest({
                            input: {
                                actKC2Ids: transformActInputNameToIds(actKC2Ids),
                                groupName,
                            },
                            workOrderId,
                        }),
                    );
                } else {
                    dispatch(
                        updateGroupRequest({
                            input: {
                                actKC2Ids: transformActInputNameToIds(actKC2Ids),
                                groupName,
                            },
                            actKC2GroupId,
                        }),
                    );
                }
            }
        },
        [actKC2GroupId, createGroupRequest, dispatch, updateGroupRequest, workOrderId],
    );
    const isCommit = createGroupStatus === RequestStatus.FETCHING || updateGroupStatus === RequestStatus.FETCHING;

    return {
        handleSaveGroup,
        isCommit,
    };
};
